<ng-container *vsLet="claim$ | async as claim">
  <div *ngIf="item$ | async as item" class="flex flex-col gap-4">
    <p class="font-medium text-gray-500 mb-2">Adjudication Details</p>

    <div class="flex flex-col gap-4 border rounded-xl p-6">
      <div class="flex gap-2">
        <p class="text-sm font-medium text-gray-700 py-3 basis-1/4">Decision</p>
        <p
          theme="primary"
          class="font-semibold text-gray-700 self-center px-5 py-2 rounded-lg border border-themed-200 bg-themed-50"
        >
          {{ item.status === draft ? 'Reviewing' : (item.status | titlecase) }}
        </p>
      </div>

      <vs-divider></vs-divider>

      <div class="flex gap-2">
        <p class="text-sm font-medium text-gray-700 basis-1/4">Reasoning</p>
        <p
          *ngIf="item.adjudicationReason"
          class="text-sm font-normal text-gray-600 basis-3/4 p-3 rounded-lg border max-h-[100px] overflow-y-auto scrollbar-thin"
        >
          {{ item.adjudicationReason }}
        </p>
      </div>
    </div>

    <ws-claim-item-eligibility
      *ngIf="claim && usesNewFlow(claim) && claim.warranty?.policy?.checkClaimEligibility"
      [claim]="claim"
      [item]="item"
      [canCheck]="!!(canManageAdjudication$ | async) && claim?.status === open && !item.deleted"
      [approvedAmount]="approvedAmount(decisions$ | async)"
    ></ws-claim-item-eligibility>

    <ng-container *ngIf="decisions$ | async as decisions">
      <p class="font-medium text-gray-500 mb-2">{{ config.history.latestTitle }}</p>

      <div data-testid="decision-summary" class="flex flex-col gap-4 border rounded-xl p-6">
        <div class="flex gap-2">
          <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.outcome }}</p>
          <div class="basis-3/4 flex gap-2 items-center">
            <vs-badge [theme]="decisionTheme[decisions.latest.action]" [config]="{ displayStatusIcon: true }">
              {{
                decisions.latest.action === redrafted ? config.sentBackToDraft : (decisions.latest.action | titlecase)
              }}
            </vs-badge>
            <p class="text-sm font-normal text-gray-500">{{ config.version }} {{ decisions.latest.version }}</p>
          </div>
        </div>

        <vs-divider></vs-divider>

        <div class="flex gap-2">
          <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.requested }}</p>
          <p data-testid="decision-requested" class="text-sm font-normal text-gray-600 basis-3/4">
            {{ formatAmount(decisions.latest.requestedAmount) }}
          </p>
        </div>

        <ng-container *ngIf="decisions.latest.action !== redrafted">
          <vs-divider></vs-divider>

          <div class="flex gap-2">
            <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.authorized }}</p>
            <p data-testid="decision-authorized" class="text-sm font-normal text-gray-600 basis-3/4">
              {{ formatAmount(decisions.latest.authorizedAmount) }}
            </p>
          </div>

          <vs-divider></vs-divider>

          <div class="flex gap-2">
            <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.difference }}</p>
            <p data-testid="decision-difference" class="text-sm font-normal text-gray-600 basis-3/4">
              {{ formatAmount(decisions.latest.requestedAmount - decisions.latest.authorizedAmount) }}
            </p>
          </div>
        </ng-container>

        <vs-divider></vs-divider>

        <div class="flex gap-2">
          <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.coverage }}</p>
          <div class="basis-3/4 flex flex-col gap-1">
            <p class="text-sm font-normal text-gray-600">{{ item.coverage?.title ?? config.otherCoverage }}</p>
            <ng-container *ngIf="item.coverage?.id">
              <p *ngIf="item.liabilityInfo as liability" class="text-sm font-normal text-gray-500">
                {{ config.labels.deductible }} {{ formatAmount(liability.deductible) }} · {{ config.labels.limit }}
                {{
                  formatAmount(
                    claim?.warranty?.plan?.liabilityPerClaim ? liability.limit : liability.remainingLiabilityLimit
                  )
                }}
              </p>
            </ng-container>
          </div>
        </div>

        <ng-container *ngIf="claim && usesNewFlow(claim) && !claim.warranty?.policy?.checkClaimEligibility">
          <vs-divider></vs-divider>

          <div class="flex gap-2">
            <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.eligibility }}</p>
            <p class="text-sm font-normal text-gray-600 basis-3/4">{{ config.noEligibilityLimit }}</p>
          </div>
        </ng-container>

        <vs-divider></vs-divider>

        <div class="flex gap-2">
          <p class="text-sm font-medium text-gray-700 basis-1/4">{{ config.labels.decidedBy }}</p>
          <p data-testid="decision-decided-by" class="text-sm font-normal text-gray-600 basis-3/4">
            {{ decisions.latest.createdBy?.displayName || config.someone }} on
            {{ decisions.latest.created | date: 'medium' }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <p class="text-sm font-normal text-gray-500">{{ config.history.beforeTax }}</p>
        <p class="text-sm font-normal text-gray-500">{{ config.notClaimAuthorization }}</p>
      </div>

      <p class="font-medium text-gray-500 mb-2">{{ config.history.title }}</p>

      <div data-testid="decision-history" class="flex flex-col gap-4 border rounded-xl p-6">
        <ng-container *ngFor="let decision of decisions.items; let last = last">
          <div data-testid="decision-history-entry" class="flex flex-col gap-1">
            <div class="flex gap-2 items-center">
              <vs-badge [theme]="decisionTheme[decision.action]" [config]="{ displayStatusIcon: true }">
                {{ decision.action === redrafted ? config.sentBackToDraft : (decision.action | titlecase) }}
              </vs-badge>
              <p class="text-sm font-normal text-gray-500">{{ config.version }} {{ decision.version }}</p>
              <p class="text-sm font-normal text-gray-500 ml-auto">
                {{ decision.createdBy?.displayName || config.someone }} · {{ decision.created | date: 'medium' }}
              </p>
            </div>

            <p *ngIf="decision.reasonLabel" class="text-sm font-medium text-gray-700">{{ decision.reasonLabel }}</p>
            <p *ngIf="decision.detail" class="text-sm font-normal text-gray-600">{{ decision.detail }}</p>

            <p
              *ngIf="decision.action !== redrafted"
              data-testid="decision-history-amounts"
              class="text-sm font-normal text-gray-600"
            >
              {{ config.labels.requested }} {{ formatAmount(decision.requestedAmount) }} ·
              {{ config.labels.authorized }}
              {{ formatAmount(decision.authorizedAmount) }}
            </p>
          </div>

          <vs-divider *ngIf="!last"></vs-divider>
        </ng-container>
      </div>
    </ng-container>

    <div
      *vsLet="canManageAdjudication$ | async as canManageAdjudication"
      class="flex gap-2"
      [class.justify-end]="claim?.status !== open || item.authorizationId || !canManageAdjudication || item.deleted"
      [class.justify-between]="
        claim?.status === open && !item.authorizationId && canManageAdjudication && !item.deleted
      "
    >
      <div
        tooltipStyle="light"
        [vsTooltip]="
          !readyForAdjudication(claim, item)
            ? {
                title: 'Cure Needed',
                subtitle: 'A fully finished cure is needed before you can adjudicate this claim item!',
              }
            : ''
        "
      >
        <button
          *ngIf="canManageAdjudication && claim?.status === open && !item.authorizationId && !item.deleted"
          vs-button
          appearance="outline"
          [disabled]="!readyForAdjudication(claim, item)"
          (click)="navigateTo('manage-adjudication')"
        >
          Edit
        </button>
      </div>

      <div class="flex gap-2">
        <button vs-button appearance="outline" (click)="navigateTo('cure')">Back</button>
        <button
          vs-button
          theme="primary"
          [disabled]="!item.adjudicationReason || item.status === draft"
          (click)="navigateTo('resolve')"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</ng-container>
